import { Injectable } from '@angular/core';
import { AngularFireDatabase, AngularFireObject } from '@angular/fire/compat/database';
import { Observable, Subject, firstValueFrom } from 'rxjs';
import { NGXLogger as LoggerService } from "ngx-logger";
import { PathService } from '../../core/path.service';
import { HeaderData, headerLocation, headerMeetingLink, meetingLinkVisibility, pushIdAgeMs } from 'whocan-lib';
import { CurrentPollService } from '../../core/current-poll.service';
import { map, tap } from 'rxjs/operators';
import { TemplateService } from '../../core/poll-templates/template.service';
import firebase from 'firebase/compat/app';
import { AuthService } from '../../../user/auth.service';
import { HeaderClass } from 'whocan-lib';
import { UIMesageService } from '../../../shared/services/ui-message.service';
import { TranslationGeneralService } from '../../../shared/translation/translation.general.service';
import { PollNotFoundError } from '../../core/poll-errors';
import { ServerTimeService } from '../../../shared/services/server-time.service';
import { ConnectionStatusService } from '../../../shared/services/connection-status.service';
import { migrateThemeFieldsForLayout, ThemeFamilyMigrationResult, themeFamilyOf } from '../theme/theme-family-migration';
import { TemplateKey } from '../../core/poll-templates/poll-config/template-keys';
import { AccessGrantService } from '../../core/access-grant.service';
import { isPermissionDenied } from '../../../shared/services/db-errors';


@Injectable({
    providedIn: 'root'
})
/**
 * Service to persist to and load from FireDatabase;
 * keeps the reference to the header that is currently in use
 */
export class HeaderService {
    private _headerClass: HeaderClass
    private _isLoading = false;
    private _persistFailureHintShown = false;
    private deleteWatch: { ref: firebase.database.Reference; callback: (snapshot: firebase.database.DataSnapshot) => void } | null = null;
    private headerSettingsChangedSubject = new Subject<void>();

    get headerSettingsChanged$(): Observable<void> {
        return this.headerSettingsChangedSubject.asObservable();
    }

    notifyHeaderSettingsChanged(): void {
        this.enforceSettingInvariants();
        this.headerSettingsChangedSubject.next();
    }

    /**
     * Template defaults that only make sense for a registered owner (defaultSettingsIfRegistered
     * in template.config.ts), e.g. sendVoteConfirmation for class booking: the toggle is locked
     * for anonymous owners, so an anonymous default would be an active setting nobody can see or
     * change — and one that turns live the moment the owner registers. An owner who registers
     * later keeps the default off and switches it on himself (decision Kai 2026-09-09).
     */
    private applyDefaultsIfRegistered(pollTemplate: string): void {
        if (!this.authService.hasEmail) return
        const sendVoteConfirmation = this.templateService.getConfigAttribute('defaultSettingsIfRegistered', 'sendVoteConfirmation', pollTemplate)
        if (sendVoteConfirmation === true && this._headerClass.askForEmailAdress) {
            this._headerClass.sendVoteConfirmation = true
        }
    }

    /**
     * Subsettings must not outlive their parent setting. sendVoteConfirmation mails the
     * response to the address entered by the participant, so without askForEmailAdress
     * it has nothing to send to — reset it instead of leaving an orphaned true in the
     * header (concept-vote-confirmation-mail-2026-09-05 §3.1). Runs on every toggle
     * change, which also covers the edit-header dialog on the vote page.
     *
     * registerPeople is the group switch of the "Will you join?" block: without it the
     * counter (askForMultiplePersons), kids and vegetarians have no block to live in;
     * without the counter the kids toggle has nothing to count. So for every consumer
     * askForMultiplePersons implies registerPeople and askForKids implies
     * askForMultiplePersons (workorder-register-people-unification-2026-09-10, Task 3).
     * maxPersons (the event-wide people limit) is a subsetting of registerPeople as well:
     * without the block there is nothing to count, so the limit is removed with it
     * (workorder-header-max-persons-2026-09-10, decision 2). The counter is NOT a
     * prerequisite - without it every acceptance counts as one person.
     * Also applied in memory to every loaded header (normalizeLegacyHeader, addendum 1):
     * legacy headers can carry orphaned subsettings (vegetarians without a block, kids
     * with the counter switched off) which would otherwise show up as empty matrix
     * columns. VotesValidationService.adjustRegisterPeopleValues stays as the second
     * line of defense for the vote itself.
     */
    private enforceSettingInvariants(): void {
        const header = this._headerClass;
        if (!header) return;
        if (!header.askForEmailAdress && header.sendVoteConfirmation) {
            header.sendVoteConfirmation = false;
        }
        if (!header.registerPeople) {
            if (header.askForMultiplePersons) header.askForMultiplePersons = false;
            if (header.askForKids) header.askForKids = false;
            if (header.askForVegetarians) header.askForVegetarians = false;
            if (header.maxPersons !== undefined) header.maxPersons = undefined;
        }
        if (!header.askForMultiplePersons && header.askForKids) {
            header.askForKids = false;
        }
    }

    /**
     * Lazy migration of headers persisted before the registerPeople group switch existed
     * (concept-register-people-unification-2026-09-10 §4.1): whether the "Will you join?"
     * block was shown used to be decided by the form — the invitation form always asked,
     * the generic and the matrix form only with askForMultiplePersons. A header without
     * registerPeople gets exactly that value derived in memory, so nothing looks different
     * after the deploy; it is persisted with the next owner save (no batch migration).
     *
     * Uses the effective form incl. the template fallback, therefore
     * templateService.initTemplate(pollTemplate) must have run before. Deliberately not
     * routed through HeaderOrTemplateService — that one depends on HeaderService.
     *
     * Afterwards the setting invariants run for EVERY loaded header (addendum 1): the old
     * edit-header never reset askForKids when the counter went off and offered
     * askForVegetarians without any block in the advanced section. The matrix columns now
     * follow the subsettings only, so such orphans would appear as new columns full of
     * zeros — a visible change for existing polls that decision 5 rules out. Order: derive
     * registerPeople, then invariants, then refresh the snapshot.
     *
     * Neither the derived value nor the invariant resets count as an unsaved change: the
     * persistence snapshot is refreshed afterwards, otherwise the auto-save of the vote
     * preview would persist (and announce) a change the owner never made just for opening
     * a legacy poll. persist() always writes the whole header, so the values reach the
     * database with the next real owner save anyway.
     *
     * The derivation from `formToUse === 'invitation'` only applies to legacy headers without
     * `registerPeople` (before v7.4.88). Since the layout switch `invitation` no longer means
     * "invitation template" - new headers carry `registerPeople` explicitly and the layout
     * switch does not touch it (concept-layout-switch-invitation-2026-10-01 R11).
     */
    private normalizeLegacyHeader(): void {
        const header = this._headerClass;
        if (!header?.headerData) return;
        if (header.registerPeople === undefined) {
            const formToUse = header.formToUse ?? this.templateService.getConfigAttribute('general', 'formToUse');
            header.registerPeople = formToUse === 'invitation' || header.askForMultiplePersons === true;
        }
        this.enforceSettingInvariants();
        header.resetDataSinceLastPersistence();
    }

    /**
     * Layout switch (concept-layout-switch-invitation-2026-10-01 R2-R4): sets formToUse, migrates the
     * design fields that are bound to a theme family and reports what was reset (snackbar in the caller).
     * A switch within the same family (normal <-> table) runs without a migration.
     *
     * Unlike normalizeLegacyHeader the persistence snapshot is NOT refreshed: the switch is an owner
     * change and has to be persisted, including the migrated fields.
     */
    public changeFormToUse(formToUse: string): ThemeFamilyMigrationResult {
        const header = this._headerClass
        const before = themeFamilyOf(header.formToUse ?? <string>this.templateService.getConfigAttribute('general', 'formToUse'))
        header.formToUse = formToUse
        const after = themeFamilyOf(formToUse)
        const result = before === after
            ? { resetFields: [] }
            : migrateThemeFieldsForLayout(header.headerData, after, <string>this.templateService.getConfigAttribute('defaultSettings', 'theme'))
        this.notifyHeaderSettingsChanged()
        return result
    }

    /**
     * Converts the poll to the type Advanced, the superset of all templates: every setting, every
     * option type, every dashboard chart. One way, no way back
     * (workorder-convert-poll-to-advanced-2026-10-05).
     *
     * Before the key changes the effective layout is written into the header: without header.formToUse
     * the layout comes from general.formToUse of the TEMPLATE (HeaderOrTemplateService.getFormToUse).
     * An invitation without a materialized formToUse would silently fall into the normal layout once
     * the template is advanced (general.formToUse 'genericWithTheme') and with it change the theme
     * family. With the layout materialized the family stays the same, so unlike the layout switch
     * no theme migration is needed.
     *
     * The advanced defaults are deliberately NOT applied (defaultSettings / defaultSettingsIfRegistered
     * only count at creation): the poll keeps every setting, option, vote, theme and layout. Persisted
     * by the caller, like changeFormToUse.
     *
     * Location and meeting link stay where they were: an invitation or potluck shows them without a toggle,
     * advanced only with showLocationAdvanced / showMeetingLinkAdvanced (headerLocation / headerMeetingLink, calendar
     * quick wins C). A shown field therefore gets its toggle switched on - otherwise it would vanish from the vote
     * page, the calendar and the mail with the conversion. The meeting link only when the old template offered it
     * and showed it (C2): an invitation or potluck keeps a stored old link hidden - its toggle is off after the
     * conversion, even if a direct write had set it - and the owner can switch it on in Advanced.
     */
    public convertToAdvanced(): void {
        const header = this._headerClass
        if (!header.formToUse) {
            // resolved BEFORE initTemplate(advanced): it has to be the layout of the OLD template
            header.formToUse = <string>this.templateService.getConfigAttribute('general', 'formToUse')
        }
        const flags = this.templateService.templateSettingFlags()
        if (!flags.locationAdvanced && headerLocation(header.headerData, { hasAdvancedToggle: false })) {
            header.showLocationAdvanced = true
        }
        if (!flags.meetingLinkAdvanced && headerMeetingLink(header.headerData, meetingLinkVisibility(flags))) {
            header.showMeetingLinkAdvanced = true
        } else if (!flags.meetingLinkAvailable && header.showMeetingLinkAdvanced) {
            header.showMeetingLinkAdvanced = false
        }
        header.pollTemplate = TemplateKey.advanced
        this.templateService.initTemplate(TemplateKey.advanced)
        this.notifyHeaderSettingsChanged() // runs the setting invariants: expected no-op, guards legacy headers
    }

    constructor(
        private logger: LoggerService,
        private pathService: PathService,
        private afd: AngularFireDatabase,
        private authService: AuthService,
        private currentPollService: CurrentPollService,
        private templateService: TemplateService,
        private uiMessage: UIMesageService,
        private trans: TranslationGeneralService,
        private serverTimeService: ServerTimeService,
        private connectionStatus: ConnectionStatusService,
        private accessGrantService: AccessGrantService,
    ) {
        this._headerClass = new HeaderClass(
            this.templateService.getDefaultSettings(),
        )
    }

    public initNewHeader(pollTemplate, pollTitle, themeKey?: string) {
        this._headerClass = new HeaderClass(
            this.templateService.getDefaultSettings(),
            pollTemplate,
            pollTitle,
            themeKey
        )
        // new polls always carry the group switch explicitly - undefined is reserved for legacy headers
        if (this._headerClass.registerPeople === undefined) this._headerClass.registerPeople = false
        this.applyDefaultsIfRegistered(pollTemplate)
    }

    get headerClass(): HeaderClass {
        return this._headerClass
    }

    get observable(): Observable<HeaderData> {
        if (!this.currentPollService.isInitialized) {
            this.logger.warn('HeaderService.observable: CurrentPollService not initialized');
            return new Observable<HeaderData>();
        }
        const ref: AngularFireObject<HeaderData> = this.afd.object(this.pathService.getHeaderPath());
        return ref.valueChanges().pipe(
            map(headerData => {
                return headerData
            }));
    }

    public async load() {
        if (!this._isLoading) {
            this._isLoading = true;
            // finally: a denied read must not leave the flag set - the next load (the auth-window retry of
            // PollService.initPollAndSubscribeData, a later poll) would silently skip and keep the old header
            try {
                this._headerClass = new HeaderClass(await firstValueFrom(this.observable))
                this._headerClass.resetDataSinceLastPersistence()
                this.templateService.initTemplate(this.headerClass.pollTemplate);
                this.normalizeLegacyHeader(); // last step: needs the template initialized
                this.watchForDelete(this.currentPollService.ownerId, this.currentPollService.pollId);
            } finally {
                this._isLoading = false;
            }
        }
    }

    get isLoading(): boolean {
        return this._isLoading
    }

    /**
     * Writes the whole header of the currently initialized poll.
     * Resolves to true only when the update reached the database.
     *
     * The poll identity is bound once at the start and never read from CurrentPollService
     * again: persist awaits the update round trip and the auto-save of the vote preview
     * fires from ngOnDestroy without an await, so it outlives the page it was started from.
     * A logout or a poll switch in between resets CurrentPollService - which is why a failed
     * write used to be reported as "ownerId: null; pollId: null" although it went to a valid
     * path (workorder-header-persist-permission-denied-2026-09-12).
     *
     * Everything up to ref.update() is synchronous: the server time comes from the offset
     * the SDK holds in memory, not from a round trip. That is what makes the beforeunload /
     * visibilitychange save of the vote preview reach the database at all - a handler is
     * done as soon as it returns, and a write that is only formulated after an await lands
     * in a document the browser is already tearing down
     * (workorder-server-time-offset-unload-save-2026-09-17).
     */
    public async persist(): Promise<boolean> {
        this.logger.log('persist: ', this.headerClass);
        if (!this.currentPollService.isInitialized) {
            this.logger.warn('HeaderService.persist: CurrentPollService not initialized');
            return false;
        }
        // Identity, header and display name are bound together: load() replaces _headerClass
        // with a new instance on a poll switch, and persist keeps running over the update round
        // trip out of the ngOnDestroy of the vote preview (fire-and-forget). Without this binding the
        // trailing save writes time stamps into the header of the next poll and takes its
        // baseline (workorder-header-persist-poll-id-inconsistent-2026-09-17). The display name
        // belongs to the start as well - after a logout the getter returns undefined.
        const header = this.headerClass;
        const ownerId = this.currentPollService.getOwnerId();
        const pollId = this.currentPollService.getPollId();
        const ownerName = this.authService.displayName;
        if (!header?.headerData) {
            this.logger.warn('HeaderService.persist: skipped, header uninitialized', ownerId, pollId);
            return false;
        }
        // with the header bound, a mismatch is no longer a race but a header that really
        // carries foreign ids - never write it, but a throw in a fire-and-forget path helps nobody.
        // Checked before anything is set on the header, so it stays untouched.
        if ((header.ownerId && ownerId !== header.ownerId) || (header.pollId && pollId !== header.pollId)) {
            this.logger.error('HeaderService.persist: header identity does not match the current poll, write dropped.'
                + ' bound: ' + ownerId + '/' + pollId + '; header: ' + header.ownerId + '/' + header.pollId);
            return false;
        }
        // A poll deleted in this session is never written again: a full header update onto the deleted path would
        // create a header without options - between 19.10. and 28.10. 14 deleted prod polls came back that way, since
        // the slug rollout the voteSlug validation rejected the same write as "update failed"
        // (workorder-header-persist-and-poll-init-denied-with-user-2026-11-03, task 0). The baseline goes along, so the
        // next auto-save tick finds nothing to save.
        if (this.currentPollService.isDeleted(ownerId, pollId)) {
            this.logger.warn('HeaderService.persist: skipped, poll deleted', ownerId, pollId);
            header.resetDataSinceLastPersistence();
            return false;
        }
        // From here on persist works on the snapshot only: unInitializePoll() empties the bound
        // instance (HeaderClass.uninitialize()) and CurrentPollService points elsewhere after a
        // poll switch - neither may drop a save that has already started
        // (workorder-header-persist-decouple-instance-2026-09-17). Since the server time is
        // read synchronously the path from here to ref.update() has no await any more, so the
        // window between binding and write is the write itself.
        // The JSON round trip also strips undefined properties - firebase rejects the whole
        // update when a single one is undefined (e.g. notificationRecipient without an email).
        const payload: HeaderData = JSON.parse(JSON.stringify(header.headerData));
        // the participant link slug belongs to VoteSlugService (targeted updates, index first). Its validation checks the
        // value against the pollSlugs index, so a header save must not carry it - not even unchanged; update() without
        // the keys leaves the stored fields alone. Removed before the baseline (withoutPersistenceFields drops them too)
        delete payload.voteSlug;
        delete payload.voteSlugAliases;
        payload.ownerName = ownerName;
        // comparison base for the baseline, taken before the time stamps are added
        const persistedPayload = JSON.stringify(payload);
        // notificationRecipient is deprecated since concept 2026-09-01 - the functions ignore it
        // (vote-notifications.ts, private-comment-mail.ts, poll-deadline.service.ts) and the rule
        // rejects a value since rules-hardening-1 (.validate: false). null deletes it with the next
        // save of every poll. Set after the baseline: it is no content edit of the header
        payload.notificationRecipient = null;
        // a plain number, never ServerValue.TIMESTAMP: the sentinel fires onHeaderChange twice
        // (local estimate, then server value) and exports two BigQuery rows per change
        const serverTime = this.serverTimeService.now();
        if (!payload.createdAt) { payload.createdAt = serverTime } // if its a new header we need to set the createdAt
        payload.updatedAt = serverTime;

        const uid = this.authService.uid;
        // Last gate before the write: the database rules know only the auth, not the current
        // poll. A logout or an identity switch in another tab makes the write a guaranteed
        // PERMISSION_DENIED - the expected end of an editing session, not an application
        // failure. Hence warn (dev and prod log to /uilogging from ERROR up, so it stays local)
        // and drop the write. A poll SWITCH on the other hand does not change the right to
        // write the bound poll and must not drop the save.
        // grant aware since 1.2: owner / legacy from the token, co-owner / editor from the cached grant role
        if (!uid || !this.accessGrantService.canEditPoll(ownerId, pollId)) {
            this.logger.warn('HeaderService.persist: skipped, auth changed while persisting',
                ownerId, pollId, 'auth uid:', uid);
            return false;
        }
        const grantRole = this.accessGrantService.hasGrantRole(ownerId, pollId);
        const ref: AngularFireObject<HeaderData> = this.afd.object(this.pathService.getHeaderPath(ownerId, pollId));
        try {
            await ref.update(payload);
        } catch (error) {
            const currentUid = this.authService.uid;
            if (!currentUid || !this.accessGrantService.canEditPoll(ownerId, pollId)) {
                // the auth went away after the gate - the same expected case, only later
                this.logger.warn('HeaderService.persist: update failed after the auth changed; header stays dirty.',
                    ownerId, pollId, 'auth uid:', currentUid, error);
            } else if (grantRole && isPermissionDenied(error)) {
                // the write went through a grant and the rules refused it: the key was rotated or the grant
                // removed since the role was resolved - a user state, re-resolved by CurrentPollService
                // (workorder-poll-scoped-access-1-2, rule 6), no error report for every rotation
                this.accessGrantService.invalidate(ownerId, pollId);
                this.logger.warn('HeaderService.persist: grant no longer valid, update denied; header stays dirty.',
                    ownerId, pollId, 'auth uid:', currentUid, error);
            } else {
                // a real rule rejection: owner and auth still match, so the cause is a .validate
                // violation, a delegation missing from the id token or a connection whose auth is not
                // yet effective for a poll created a moment ago (first write of a fresh anonymous
                // session). The prefix is fixed - Error Reporting grouping and the triage register
                // key on it; the fields behind it are what tells the mechanisms apart
                // (workorder-persist-new-poll-single-flight-2026-09-22, task 5). The fields before the probe are read
                // synchronously at the moment of the rejection.
                const diagnostics = 'HeaderService.persist: update failed; header stays dirty. ownerId: '
                    + ownerId + '; pollId: ' + pollId + '; auth uid: ' + uid
                    + '; currentUser: ' + (this.authService.sdkCurrentUserUid ?? 'null')
                    + '; tokenIssuedAt: ' + (this.authService.tokenIssuedAtTime ?? 'null')
                    + '; tokenExpiresInMs: ' + (this.authService.tokenExpiresInMs ?? 'null')
                    + '; msSinceAuth: ' + (this.authService.msSinceAuthStateChange ?? 'null')
                    + '; connected: ' + this.connectionStatus.connected
                    + '; online: ' + globalThis.navigator?.onLine
                    + '; pollAgeMs: ' + pushIdAgeMs(pollId)
                    + '; slugFields: ' + slugFieldsOf(payload)
                    + '; payloadKeys: ' + Object.keys(payload).length;
                // a header that never reached the database (first write of a new poll) reads as null as well - that is
                // the auth window of persistNewPoll (it retries with the same push id), not a delete
                const wasPersisted = !!header.createdAt;
                const recovered = await this.recoverRejectedPersist(ref, payload, ownerId, pollId, wasPersisted, diagnostics, error);
                if (recovered) {
                    this.syncPersistedHeader(header, payload, persistedPayload);
                    return true;
                }
            }
            return false;
        }
        this.syncPersistedHeader(header, payload, persistedPayload);
        return true;
    }

    /**
     * Follow-up of a real rule rejection (workorder-header-persist-and-poll-init-denied-with-user-2026-11-03, task 3a):
     * 1. probe - a READ of the header, not the `updatedAt` write the workorder sketched: on a deleted poll that write
     *    would itself create a header stub. Read = `auth != null`, so `denied` means the connection carries no auth,
     *    `ok` means the payload failed a .validate, `deleted` means the poll is gone (marked, no report, no hint),
     *    `missing` a new poll whose first write was rejected (auth window, persistNewPoll retries it);
     * 2. one forced token refresh (AuthService.forceTokenRefreshOnce, at most once a minute) - it sends the database
     *    connection a new auth frame - and the original update once more;
     * 3. the error line with `probe` and `retryAfterTokenRefresh` behind the fixed prefix - also when the retry went
     *    through, that pair is the evidence for a connection without auth;
     * 4. the save failure hint once per session (the vote preview skips its own one then).
     * Resolves to true when the retry reached the database.
     */
    private async recoverRejectedPersist(ref: AngularFireObject<HeaderData>, payload: HeaderData, ownerId: string,
        pollId: string, wasPersisted: boolean, diagnostics: string, error: unknown): Promise<boolean> {
        let probe: 'ok' | 'denied' | 'deleted' | 'missing' | 'failed';
        try {
            const snapshot = await this.afd.database.ref(this.pathService.getHeaderPath(ownerId, pollId)).once('value');
            probe = snapshot.exists() ? 'ok' : wasPersisted ? 'deleted' : 'missing';
        } catch (probeError) {
            probe = isPermissionDenied(probeError) ? 'denied' : 'failed';
        }
        if (probe === 'deleted') {
            this.currentPollService.markDeleted(ownerId, pollId);
            this.headerClass?.resetDataSinceLastPersistence();
            this.logger.warn('HeaderService.persist: update denied, the poll was deleted meanwhile; write dropped.', ownerId, pollId);
            return false;
        }
        let retryAfterTokenRefresh: 'ok' | 'denied' | 'failed' | 'skipped' = 'skipped';
        if (await this.authService.forceTokenRefreshOnce()) {
            try {
                await ref.update(payload);
                retryAfterTokenRefresh = 'ok';
            } catch (retryError) {
                retryAfterTokenRefresh = isPermissionDenied(retryError) ? 'denied' : 'failed';
            }
        }
        this.logger.error(diagnostics + '; probe: ' + probe + '; retryAfterTokenRefresh: ' + retryAfterTokenRefresh, error);
        if (retryAfterTokenRefresh === 'ok') return true;
        // not for a new poll: persistNewPoll retries and shows its own error when the creation fails
        if (!this._persistFailureHintShown && probe !== 'missing') {
            this._persistFailureHintShown = true;
            this.uiMessage.warning(this.templateService.getLabel('header', 'autoSaveFailedMessage'));
        }
        return false;
    }

    /** the save failure hint of a rule rejection was shown in this session (max. one proactive hint) */
    get persistFailureHintShown(): boolean {
        return this._persistFailureHintShown;
    }

    /** the initialized poll was deleted in this session - its saves are dropped (auto-save: nothing to save) */
    get isCurrentPollDeleted(): boolean {
        return this.currentPollService.isCurrentPollDeleted;
    }

    /**
     * Notices a delete of the loaded poll in another tab or by a co-organizer: a listen on `createdAt` only (small,
     * and not the header path itself, which load() reads with its own listen). A value that existed and is gone marks
     * the poll as deleted - the trailing saves of this tab are then dropped instead of bringing the poll back. Only
     * for those who may edit; a header without createdAt (legacy) is never marked. A cancelled listen is a warn.
     */
    private watchForDelete(ownerId: string, pollId: string): void {
        this.stopDeleteWatch();
        if (!ownerId || !pollId || !this.accessGrantService.canEditPoll(ownerId, pollId)) return;
        const ref = this.afd.database.ref(this.pathService.getHeaderPath(ownerId, pollId) + '/createdAt');
        let existed = false;
        const callback = ref.on('value', snapshot => {
            if (snapshot.exists()) {
                existed = true;
            } else if (existed && !this.currentPollService.isDeleted(ownerId, pollId)) {
                this.logger.warn('HeaderService: the poll was deleted elsewhere, its saves are dropped', ownerId, pollId);
                this.currentPollService.markDeleted(ownerId, pollId);
            }
        }, (watchError: Error) => {
            this.logger.warn('HeaderService: delete watch cancelled', ownerId, pollId, (watchError as { code?: string })?.code ?? watchError?.message);
        });
        this.deleteWatch = { ref, callback };
    }

    /** PollService.unInitializePoll and every load() */
    public stopDeleteWatch(): void {
        if (this.deleteWatch) {
            this.deleteWatch.ref.off('value', this.deleteWatch.callback);
            this.deleteWatch = null;
        }
    }

    /**
     * Brings the bound header in line with what reached the database. A header emptied by
     * unInitializePoll() is left alone - nobody reads its state any more. A header that load()
     * has already replaced is still synced: it only costs a few assignments on an orphan and
     * keeps the state right for anyone still holding the instance.
     */
    private syncPersistedHeader(header: HeaderClass, payload: HeaderData, persistedPayload: string): void {
        if (!header.headerData) return;
        // poll-results reads createdAt from the in-memory header
        if (!header.createdAt) { header.createdAt = payload.createdAt }
        header.updatedAt = payload.updatedAt;
        header.ownerName = payload.ownerName;
        // only take a new baseline if the content was not edited again while the write was in
        // flight - the fields persist sets itself do not count as an edit
        const persisted = withoutPersistenceFields(JSON.parse(persistedPayload));
        const current = withoutPersistenceFields(JSON.parse(JSON.stringify(header.headerData)));
        if (JSON.stringify(current) === JSON.stringify(persisted)) {
            header.resetDataSinceLastPersistence();
        }
    }

    /**
     * Reads the close state straight from the database instead of the in-memory
     * header. load() runs once per session, so a tab that was already open when
     * the owner closed the poll still holds the stale open state.
     *
     * Does not touch the current headerClass on purpose: the owner may have
     * unsaved header edits open in the preview sidebar.
     * Returns false when the state can not be read — the database rules still
     * refuse the write, so the vote is never silently lost.
     */
    public async isClosedInDatabase(): Promise<boolean> {
        if (!this.currentPollService.isInitialized) return false;
        try {
            const snapshot = await this.afd.database.ref(this.pathService.getHeaderPath()).once('value');
            const headerData: HeaderData = snapshot.val();
            if (!headerData) return false;
            return new HeaderClass(headerData).isClosed;
        } catch (error) {
            this.logger.warn('isClosedInDatabase: could not read header', error);
            return false;
        }
    }

    /**
     * Central close/reopen entry point — every UI touchpoint (context-action-bar,
     * my-polls menu, edit-header status row) delegates here, no duplicated logic.
     * Both methods require a loaded header: without pollId/ownerId a click before
     * load completion would persist the default HeaderClass onto the poll path.
     */
    public async closePoll(): Promise<void> {
        if (!this.headerClass?.pollId || !this.headerClass?.ownerId) {
            this.logger.warn('closePoll: header not loaded');
            return;
        }
        this.headerClass.closed = true;
        // the success only when the write went through - a rejection shows the save failure hint in persist
        if (await this.persist()) this.uiMessage.success(this.trans.translate('poll.closedSuccess'));
    }

    public async reopenPoll(): Promise<void> {
        if (!this.headerClass?.pollId || !this.headerClass?.ownerId) {
            this.logger.warn('reopenPoll: header not loaded');
            return;
        }
        this.headerClass.closed = false;
        let autoCloseDisabled = false;
        if (this.headerClass.autoCloseAfterRespondTill && this.headerClass.isRespondTillExpired) {
            // otherwise the poll would snap shut again immediately
            this.headerClass.autoCloseAfterRespondTill = false;
            autoCloseDisabled = true;
        }
        if (!(await this.persist())) return;
        if (autoCloseDisabled) {
            this.uiMessage.info(this.trans.translate('poll.reopenedAutoCloseOff'));
        } else {
            this.uiMessage.success(this.trans.translate('poll.reopenedSuccess'));
        }
    }

    /**
     * Resolves once the SDK reports `.info/connected === true`, or after the timeout (never rejects).
     * Used before the single retry of a denied first header write.
     */
    public waitForConnected(timeoutMs: number): Promise<boolean> {
        return this.connectionStatus.waitForConnected(timeoutMs);
    }

    /** `connectedAgeMs: …; clockSkewMs: …` for the poll init report (workorder 2026-11-03, task 3b) - diagnostics only */
    public get connectionTimingDiagnostics(): string {
        return 'connectedAgeMs: ' + (this.connectionStatus.msSinceConnected ?? 'null')
            + '; clockSkewMs: ' + (this.serverTimeService.clockSkewMs ?? 'null');
    }

    /** the last `.info/connected` value (undefined before the first one) - diagnostics only */
    public get connected(): boolean | undefined {
        return this.connectionStatus.connected;
    }

    /**
     * A fresh push id under the owner's header node. push() without a value only generates the key
     * on the client - nothing is sent to the server (push({}) used to send an empty write frame).
     */
    public getNewHeaderKey(ownerId?: string): string {
        const newKey = this.afd.database.ref(this.pathService.getPollsOwnedPath(ownerId)).push().key;
        this.logger.log('getNewHeaderKey - key: ' + newKey);
        return newKey;
    }


    /**
     * @param reportReadError false when the caller reports a failed read itself (PollService, with the
     *   auth-window diagnostics) - the read error is then only a warn here, so one failure is one report
     */
    public async headerExist(ownerId: string, pollId: string, reportReadError = true): Promise<boolean> {
        this.logger.log('headerExists? start; ownerId: ' + ownerId + ' surveId: ' + pollId);
        const promise = new Promise<boolean>((resolve, reject) => {
            let ref: firebase.database.Reference;
            try {
                ref = this.afd.database.ref(this.pathService.getHeaderPath(ownerId, pollId));
            } catch (error) {
                // firebase throws synchronously on an invalid key (. # $ [ ] control chars) -
                // that is a bad link, not a server problem: a user state, never a reported error
                this.logger.warn('headerExist: invalid path', ownerId, pollId, error?.message);
                reject(new PollNotFoundError(ownerId, pollId, 'invalid poll path'));
                return;
            }
            const pending = ref.once('value', snapshot => {
                if (snapshot.exists()) {
                    this.logger.log('headerExists exists: ');
                    resolve(true);
                } else {
                    this.logger.log('headerExists doesn\'t exist');
                    resolve(false);
                }
            }, error => {
                // real network / permission failure - without this callback the promise
                // would never settle and the page would hang in the loading spinner
                if (reportReadError) {
                    this.logger.error('headerExist: read failed. ' + error);
                } else {
                    this.logger.warn('headerExist: read failed. ' + error);
                }
                reject(error);
            });
            // once() also returns a promise that rejects on a cancelled listen - handled by the callback above,
            // otherwise every denial adds an "Uncaught (in promise)" (auth-drop hotfix, dev check 1h)
            Promise.resolve(pending).catch(() => { });
        });
        return promise;
    }

    /**
     * @returns new pollId
     */
    public async copyHeader(ownerId: string, pollId: string, newOwnerId: string, newOwnerName: string): Promise<HeaderClass> {
        this.logger.log('Exists? start; ownerId: ' + ownerId + ' surveId: ' + pollId);
        const promise = new Promise<HeaderClass>(async (resolve, reject) => {
            try {
                const ref = this.afd.database.ref(this.pathService.getHeaderPath(ownerId, pollId));
                ref.once('value', async snapshot => {
                    if (snapshot.exists()) {
                        this.logger.log('Survey exists: snapshot.val() header?', snapshot.val());
                        const copyFromHeaderData: HeaderData = snapshot.val()
                        const newHeader = await this.copyAfdHeaderObject(copyFromHeaderData, newOwnerId, newOwnerName);
                        resolve(newHeader);
                    } else {
                        this.logger.log('Survey doesn\'t exist');
                        resolve(null);
                    }
                });
            } catch (error) {
                const errorMessage = 'Could not reach server. ' + error;
                this.logger.error(errorMessage);
                reject(error);
            }
        });
        return promise;
    }

    /**
     * @returns the new HeeaderClass
     */
    private async copyAfdHeaderObject(copyFromHeaderData: HeaderData, newOwnerId: string, newOwnerName: string): Promise<HeaderClass> {
        const newPollId = await this.getNewHeaderKey(newOwnerId);
        const newHeaderData = { ...copyFromHeaderData, };
        newHeaderData.pollTitle = 'Copy of ' + copyFromHeaderData.pollTitle;
        newHeaderData.ownerId = newOwnerId;
        newHeaderData.ownerName = newOwnerName;
        newHeaderData.pollId = newPollId;
        newHeaderData.votesCounter = 0;
        // rules-hardening-1: the copy never carries the deprecated free-text recipient (.validate: false), and only an
        // admin may write isSamplePoll === true - a copy of a sample poll is a normal poll (SamplePollCopyService
        // clears slug and pollLanguage afterwards, the admin page marks a sample poll explicitly)
        delete newHeaderData.notificationRecipient;
        // the participant link slug stays with the source: the index points at the original, the header validation
        // would refuse the copy. The copy gets its own proposal on its first share (concept §6.1)
        delete newHeaderData.voteSlug;
        delete newHeaderData.voteSlugAliases;
        if (newHeaderData.isSamplePoll === true) { newHeaderData.isSamplePoll = false }
        const serverTime = this.serverTimeService.now()
        newHeaderData.updatedAt = serverTime
        newHeaderData.createdAt = serverTime
        const ref: AngularFireObject<HeaderData> = this.afd.object(this.pathService.getHeaderPath(newOwnerId, newPollId));
        await ref.update(newHeaderData);
        this.logger.log('copyAFDHeaderObject: new object with ID: ' + newPollId + ' has been created.');
        return new HeaderClass(newHeaderData);
    }

    /** awaited by deletePoll — a list reload right after the delete must not overtake the write */
    public async deleteHeader(ownerId: string, pollId: string): Promise<void> {
        const ref: AngularFireObject<any> = this.afd.object(this.pathService.getHeaderPath(ownerId, pollId));
        await ref.remove();
        this.logger.log('deleteHeader: ownerId:  ' + ownerId + '; pollId: ' + pollId);
    }

    /**
     * Deletes header, options, votes and votePrivate of a poll in ONE multi-path update.
     * The header must not outlive the votes: the vote-confirmation trigger on votePrivate
     * reads a missing header as "poll deleted" and stays silent — a sequential delete
     * (votes first, header last) would mail every participant that their response was
     * removed (workorder-vote-confirmation-mail-2026-09-05, Task 5 step 1).
     */
    public async deletePollData(ownerId: string, pollId: string): Promise<void> {
        await this.afd.database.ref().update({
            [this.pathService.getHeaderPath(ownerId, pollId)]: null,
            [this.pathService.getOptionsPath(ownerId, pollId)]: null,
            [this.pathService.getVotesPath(ownerId, pollId)]: null,
            [this.pathService.getVotePrivatePath(ownerId, pollId)]: null,
        });
        this.logger.log('deletePollData: ownerId:  ' + ownerId + '; pollId: ' + pollId);
    }
}

/** which slug fields a write carries - presence only, never the value (since task 2 always 'none' for persist) */
function slugFieldsOf(data: HeaderData): 'none' | 'voteSlug' | 'aliases' | 'both' {
    const hasSlug = data.voteSlug !== undefined;
    const hasAliases = data.voteSlugAliases !== undefined;
    return hasSlug && hasAliases ? 'both' : hasSlug ? 'voteSlug' : hasAliases ? 'aliases' : 'none';
}

/** the fields persist sets on its own - they are not a content edit of the header */
function withoutPersistenceFields(data: HeaderData): HeaderData {
    // the slug fields are not part of the payload (persist) - comparing them would keep every header with a slug dirty
    const { updatedAt, createdAt, ownerName, voteSlug, voteSlugAliases, ...content } = data;
    return content;
}
